import React from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import MagicButton from 'components/MagicButton/MagicButton';
import { AssetUrls } from 'config/assets';
import { toast } from 'react-toastify';
import { useCancelSubscriptionMutation } from 'store/services/subscription/subscriptionApi';
import { meApi } from 'store/services/user/meApi';
import './CancelSubscriptionModal.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  currentPeriodEnd: string | null;
}

const CancelSubscriptionModal: React.FC<Props> = ({ isOpen, onClose, currentPeriodEnd }) => {
  const dispatch = useDispatch();
  const [cancelSubscription, { isLoading }] = useCancelSubscriptionMutation();

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'your current billing period';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleConfirmCancel = async () => {
    try {
      await cancelSubscription().unwrap();
      dispatch(meApi.util.invalidateTags(['User']));
      toast.success('Subscription cancelled. Your Elite benefits will remain active until the end of your billing period.');
      onClose();
    } catch {
      toast.error('Failed to cancel subscription. Please try again.');
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="cancel-sub-modal-overlay" onClick={onClose}>
      <div className="cancel-sub-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cancel-sub-modal-header">
          <h2 className="cancel-sub-modal-title">Cancel Subscription</h2>
          <button className="cancel-sub-close-button" onClick={onClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="cancel-sub-modal-body">
          <p className="cancel-sub-description">
            Your Elite benefits will remain active until <strong>{formatDate(currentPeriodEnd)}</strong>. After that, your subscription will not renew.
          </p>
          <div className="cancel-sub-info-list">
            <div className="cancel-sub-info-item">
              Your badge selections and cosmetic preferences will be saved if you resubscribe later
            </div>
            <div className="cancel-sub-info-item">
              You can resubscribe at any time
            </div>
          </div>
        </div>

        <div className="cancel-sub-modal-footer">
          <MagicButton
            label="Keep Subscription"
            color="purple"
            onPress={onClose}
            isDisabled={isLoading}
          />
          <MagicButton
            label={isLoading ? 'Loading...' : 'Cancel Subscription'}
            color="dark-grey"
            onPress={handleConfirmCancel}
            isLoading={isLoading}
            isDisabled={isLoading}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CancelSubscriptionModal;
